import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  TrendingUp,
  Package,
  MessageSquare,
  DollarSign,
  ShieldCheck,
  Infinity,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { SellerMessagesCount } from "../../store/partChat/partChatActions";
import { resetSellerCount } from "../../store/partChat/partChatSlice";
import { getEffectivePlan } from "../../utils/subscriptionUtils";

const StatCard = ({ title, value, icon, trend, note, colorClass }) => {
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card
        className={`shadow-lg border-l-4 h-full flex flex-col ${
          colorClass || "border-primary"
        }`}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{value}</div>
          {trend && (
            <p
              className={`text-xs ${
                trend > 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {trend > 0 ? `+${trend}%` : `${trend}%`} from last month
            </p>
          )}
          {note && <p className="text-xs mt-1 text-muted-foreground">{note}</p>}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const DashboardStats = ({
  inventoryCount,
  messageCount,
  totalSales,
  salesTrend,
  planTier,
  partLimit = 10,
  messageLimit = 30,
}) => {
  const dispatch = useDispatch();
  const { user, currentSubscription } = useSelector((state) => state.auth);
  const { sellerMessagesCount, isSellerCountLoading } = useSelector(
    (state) => state.partChat,
  );

  const currentPlan = getEffectivePlan(currentSubscription);
  const planColors = {
    free: "border-gray-500",
    basic: "border-blue-500",
    premium: "border-purple-500",
    pro: "border-green-500",
  };

  useEffect(() => {
    if (user?.role == "seller") {
      dispatch(resetSellerCount());
      dispatch(SellerMessagesCount(user?.uid));
    }
  }, []);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <StatCard
        title="Total Inventory Items"
        value={
          <>
            {inventoryCount || 0} /{" "}
            {partLimit === "unlimited" ? (
              <Infinity className="inline h-4 w-4 text-muted-foreground" />
            ) : (
              partLimit
            )}
          </>
        }
        icon={<Package className="h-5 w-5 text-muted-foreground" />}
        note="Current active listings"
        colorClass="border-blue-500"
      />
      <StatCard
        title={
          currentPlan == "free" ? "Total Messages Limit" : "Unread Messages"
        }
        value={
          <>
            {messageLimit === "unlimited" && !isSellerCountLoading
              ? messageCount
              : sellerMessagesCount?.count || 0}
            {messageLimit !== "unlimited" && ` / ${30}`}
          </>
        }
        icon={<MessageSquare className="h-5 w-5 text-muted-foreground" />}
        note={
          currentPlan == "free"
            ? "Message quota for Free plan sellers"
            : "New inquiries from potential buyers"
        }
        colorClass="border-orange-500"
      />

      {/* <StatCard
        title="Total Sales (Month)"
        value={`$${(totalSales || 0).toFixed(2)}`}
        icon={<DollarSign className="h-5 w-5 text-muted-foreground" />}
        trend={salesTrend}
        colorClass="border-green-500"
      /> */}

      {/* Plan */}
      <StatCard
        title="Current Plan"
        value={
          <span className="capitalize">
            {planTier && planTier.length > 0
              ? planTier[1]?.previousPlan
                ? `${planTier[1].previousPlan} → ${planTier[0].currentPlan}`
                : planTier[0].currentPlan
              : "N/A"}
          </span>
        }
        icon={<ShieldCheck className="h-5 w-5 text-muted-foreground" />}
        note={
          planTier && planTier[0]?.currentPlan
            ? planTier[1]?.previousPlan
              ? `You downgraded from ${planTier[1].previousPlan} Tier. When your current period ends, your plan will switch to ${planTier[0].currentPlan} Tier.`
              : planTier[0].currentPlan.toLowerCase() === "free"
                ? "You are on the Free plan. Upgrade for more features."
                : `You are on the ${planTier[0].currentPlan} plan.`
            : "Active subscription"
        }
        colorClass={
          planColors[planTier?.[0]?.currentPlan?.toLowerCase()] ||
          "border-primary"
        }
      />
    </div>
  );
};

export default DashboardStats;
